import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  ExperimentOutlined,
  ExportOutlined,
  EyeOutlined,
  FileTextOutlined,
  GlobalOutlined,
  LinkOutlined,
  LockOutlined,
  ReloadOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Input,
  Layout,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { message } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AIPromptTestingModal from "@/Components/Common/AIPromptTestingModal";
import { getLocalStorageItem } from "@/Utils/Index";

const { Text, Link } = Typography;
const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;

function ResumeData() {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // For immediate UI feedback
  const [filters, setFilters] = useState({
    fileType: "",
    userType: "",
    createdDate: "",
    sortOrder: "latest", // latest or oldest
    limitCount: "", // number of resumes to show
    formUses: "", // filter by form edit usage count
    hasPreview: "", // filter by preview status
    hasShare: "", // filter by share status
    hasThankYou: "", // filter by thank you status
    isPublic: "", // filter by public resume status
    hasCoverLetter: "", // filter by cover letter status
    resumeSource: "", // filter by resume source
  });

  // Debounce timer ref
  const searchTimeoutRef = useRef(null);

  // Pagination state - now server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // Default to 25 per page
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // AI Prompt Testing Modal state
  const [aiPromptModalVisible, setAiPromptModalVisible] = useState(false);
  const [selectedResumeForAI, setSelectedResumeForAI] = useState(null);

  // Export data state
  const [exportLoading, setExportLoading] = useState(false);

  // Public Resume Modal state
  const [publicResumeModalVisible, setPublicResumeModalVisible] = useState(false);
  const [selectedPublicResume, setSelectedPublicResume] = useState(null);
  const [publicResumeSettings, setPublicResumeSettings] = useState({
    showEmail: true,
    showPhone: true,
    showAddress: true,
  });
  const [publicResumeLoading, setPublicResumeLoading] = useState(false);

  // Source Details Modal state
  const [sourceDetailsModal, setSourceDetailsModal] = useState({ visible: false, record: null });

  useEffect(() => {
    const accessToken = getLocalStorageItem("authorizationAdminPanel");
    if (!accessToken) {
      navigate("/app/admin/login");
    } else {
      fetchResumeData();
    }
  }, []);

  // Update when page size changes
  useEffect(() => {
    if (currentPage === 1) {
      fetchResumeData(1, pageSize, searchTerm, filters);
    }
  }, [pageSize]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchResumeData = async (
    page = currentPage,
    size = pageSize,
    search = searchTerm,
    filterParams = filters
  ) => {
    setLoading(true);
    try {
      // Build query parameters for server-side pagination and filtering
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
      });

      // Add search parameter
      if (search && search.trim()) {
        queryParams.append("search", search.trim());
      }

      // Add filter parameters
      if (filterParams.fileType) {
        queryParams.append("fileType", filterParams.fileType);
      }
      if (filterParams.userType) {
        queryParams.append("userType", filterParams.userType);
      }
      if (filterParams.createdDate) {
        queryParams.append("createdDate", filterParams.createdDate);
      }
      if (filterParams.sortOrder) {
        queryParams.append("sortOrder", filterParams.sortOrder);
      }
      if (filterParams.limitCount) {
        queryParams.append("limitCount", filterParams.limitCount);
      }
      if (filterParams.formUses) {
        queryParams.append("formUses", filterParams.formUses);
      }
      if (filterParams.hasPreview) {
        queryParams.append("hasPreview", filterParams.hasPreview);
      }
      if (filterParams.hasShare) {
        queryParams.append("hasShare", filterParams.hasShare);
      }
      if (filterParams.hasThankYou) {
        queryParams.append("hasThankYou", filterParams.hasThankYou);
      }
      if (filterParams.isPublic) {
        queryParams.append("isPublic", filterParams.isPublic);
      }
      if (filterParams.hasCoverLetter) {
        queryParams.append("hasCoverLetter", filterParams.hasCoverLetter);
      }
      if (filterParams.resumeSource) {
        queryParams.append("resumeSource", filterParams.resumeSource);
      }

      // Fetch resumes with pagination from backend
      const response = await fetch.get(`admin/resumes?${queryParams.toString()}`);

      if (response.success && response.data) {
        const { resumes, pagination } = response.data;
        const processedData = processResumeData(resumes || []);
        setData(processedData);

        // Update pagination state
        setTotalItems(pagination.total || 0);
        setTotalPages(pagination.totalPages || 0);
        setCurrentPage(pagination.current || 1);
      } else {
        setData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching resume data:", error);
      setData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Process resume data from backend to match our display format
  const processResumeData = (resumes) => {
    return resumes
      .map((resume) => {
        const personalInfo = resume.parsedData?.personalInfo || {};
        const tokenUsage = resume.tokenUsage || {};

        return {
          id: resume._id,
          resumeId: resume._id,
          // User Info fields
          userName: personalInfo.name || null,
          userEmail: personalInfo.email || null,
          userPhone: personalInfo.phone || null,
          location: personalInfo.location || null,
          linkedIn: personalInfo.linkedIn || personalInfo.linkedin || null,
          github: personalInfo.github || null,
          website: personalInfo.website || null,
          // Resume Details
          fileName: resume.fileName || "Unknown File",
          fileSize: resume.fileSize || null,
          fileType: resume.fileType || "unknown",
          fileUrl: resume.fileUrl || null,
          experienceCount: Array.isArray(resume.parsedData?.experience) ? resume.parsedData.experience.length : 0,
          educationCount: Array.isArray(resume.parsedData?.education) ? resume.parsedData.education.length : 0,
          skillsCount: Array.isArray(resume.parsedData?.skills) ? resume.parsedData.skills.length : 0,
          projectsCount: Array.isArray(resume.parsedData?.projects) ? resume.parsedData.projects.length : 0,
          certificationsCount: Array.isArray(resume.parsedData?.certifications)
            ? resume.parsedData.certifications.length
            : 0,
          // Token Usage - Enhanced with input/output breakdown and pricing
          parsingInputTokens: tokenUsage.parsingInputTokens || tokenUsage.parsingTokens || 0,
          parsingOutputTokens: tokenUsage.parsingOutputTokens || 0,
          parsingTotalTokens: tokenUsage.parsingTotalTokens || tokenUsage.parsingTokens || 0,
          chatInputTokens: tokenUsage.chatInputTokens || 0,
          chatOutputTokens: tokenUsage.chatOutputTokens || 0,
          chatTotalTokens: tokenUsage.chatTotalTokens || tokenUsage.chatTokens || 0,
          // NEW: Enhancement tokens (form-based editing)
          enhancementInputTokens: tokenUsage.enhancementInputTokens || 0,
          enhancementOutputTokens: tokenUsage.enhancementOutputTokens || 0,
          enhancementTotalTokens: tokenUsage.enhancementTotalTokens || 0,
          // NEW: Cover Letter tokens
          coverLetterInputTokens: resume.coverLetter?.tokenUsage?.inputTokens || 0,
          coverLetterOutputTokens: resume.coverLetter?.tokenUsage?.outputTokens || 0,
          coverLetterTotalTokens: resume.coverLetter?.tokenUsage?.totalTokens || 0,
          coverLetterCost: resume.coverLetter?.tokenUsage?.cost || 0,
          hasCoverLetter: !!resume.coverLetter?.generatedContent,
          // NEW: Naukri job post tokens (check both tokenUsage and aiMetadata for backwards compatibility)
          naukriInputTokens: tokenUsage.naukriInputTokens || resume.aiMetadata?.tokenUsage?.promptTokens || 0,
          naukriOutputTokens: tokenUsage.naukriOutputTokens || resume.aiMetadata?.tokenUsage?.completionTokens || 0,
          naukriTotalTokens: tokenUsage.naukriTotalTokens || resume.aiMetadata?.tokenUsage?.totalTokens || 0,
          naukriCost: tokenUsage.naukriCost || 0,
          // Flag to identify Naukri resumes
          isNaukriResume: resume.resumeSource === "naukri",
          totalInputTokens:
            (tokenUsage.parsingInputTokens || tokenUsage.parsingTokens || 0) +
            (tokenUsage.chatInputTokens || 0) +
            (tokenUsage.enhancementInputTokens || 0) +
            (resume.coverLetter?.tokenUsage?.inputTokens || 0) +
            (tokenUsage.naukriInputTokens || resume.aiMetadata?.tokenUsage?.promptTokens || 0),
          totalOutputTokens:
            (tokenUsage.parsingOutputTokens || 0) +
            (tokenUsage.chatOutputTokens || 0) +
            (tokenUsage.enhancementOutputTokens || 0) +
            (resume.coverLetter?.tokenUsage?.outputTokens || 0) +
            (tokenUsage.naukriOutputTokens || resume.aiMetadata?.tokenUsage?.completionTokens || 0),
          grandTotalTokens:
            (tokenUsage.parsingInputTokens || tokenUsage.parsingTokens || 0) +
            (tokenUsage.parsingOutputTokens || 0) +
            (tokenUsage.chatInputTokens || 0) +
            (tokenUsage.chatOutputTokens || 0) +
            (tokenUsage.enhancementInputTokens || 0) +
            (tokenUsage.enhancementOutputTokens || 0) +
            (resume.coverLetter?.tokenUsage?.inputTokens || 0) +
            (resume.coverLetter?.tokenUsage?.outputTokens || 0) +
            (tokenUsage.naukriInputTokens || resume.aiMetadata?.tokenUsage?.promptTokens || 0) +
            (tokenUsage.naukriOutputTokens || resume.aiMetadata?.tokenUsage?.completionTokens || 0),
          totalCost: 0, // Will be calculated in the render
          lastChatUpdate: tokenUsage.lastChatUpdate ? new Date(tokenUsage.lastChatUpdate) : null,
          lastEnhancementUpdate: tokenUsage.lastEnhancementUpdate
            ? new Date(tokenUsage.lastEnhancementUpdate)
            : null,
          // Status & Activity
          status: resume.status || "unknown",
          createdAt: new Date(resume.createdAt),
          updatedAt: new Date(resume.updatedAt),
          parseDate: resume.parseDate ? new Date(resume.parseDate) : null,
          processingTime: resume.processingTime || null,
          // Additional tracking
          userId: resume.userId || null,
          sessionId: resume.sessionId || null,
          isDeleted: resume.isDeleted || false,
          errorMessage: resume.errorMessage || null,
          // NEW: Preview and Share tracking
          hasPreview: resume.hasPreview || false,
          hasShare: resume.hasShare || false,
          hasThankYou: resume.hasThankYou || false,
          // NEW: Shared URL and platform tracking
          sharedUrl: resume.sharedUrl || null,
          sharedPlatform: resume.sharedPlatform || null,
          platformCode: resume.platformCode || null,
          // NEW: AI Input Usage Counting
          chatInputCount: resume.chatInputCount || 0,
          formEditInputCount: resume.formEditInputCount || 0,
          // NEW: Public Resume tracking
          isPublic: resume.isPublic || false,
          publicSlug: resume.publicSlug || null,
          madePublicAt: resume.madePublicAt ? new Date(resume.madePublicAt) : null,
          // User resume usage data (legacy)
          resumeUsedCount: resume.userResumeUsage?.resumeUsedCount || 0,
          maxResumes: resume.userResumeUsage?.maxResumes || 3,
          userPlan: resume.userResumeUsage?.userPlan || null,
          // New credit system
          userCredits: resume.userCredits || {
            balance: resume.userResumeUsage?.creditBalance ?? 0,
            totalUsed: resume.userResumeUsage?.creditsUsed ?? 0,
            totalPurchased: resume.userResumeUsage?.creditsPurchased ?? 0,
          },
          // Resume source tracking
          resumeSource: resume.resumeSource || null,
          creationMode: resume.creationMode || "upload",
          initialContext: resume.initialContext || null,
          // IMPORTANT: Include the full parsedData for AI testing
          parsedData: resume.parsedData || null,
        };
      })
      .filter((resume) => !resume.isDeleted); // Filter out deleted resumes
  };

  const formatTimeAgo = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return "N/A";
  };

  // Calculate pricing for gpt-4.1-mini model
  const calculateTokenCost = (inputTokens, outputTokens) => {
    // gpt-4.1-mini pricing: $0.80/1M input, $3.20/1M output
    const inputCost = (inputTokens / 1000000) * 0.8;
    const outputCost = (outputTokens / 1000000) * 3.2;
    return inputCost + outputCost;
  };

  const calculateParsingCost = (inputTokens, outputTokens) => {
    return calculateTokenCost(inputTokens, outputTokens);
  };

  const calculateChatCost = (inputTokens, outputTokens) => {
    return calculateTokenCost(inputTokens, outputTokens);
  };

  const calculateEnhancementCost = (inputTokens, outputTokens) => {
    return calculateTokenCost(inputTokens, outputTokens);
  };

  // Format currency with appropriate precision
  const formatCurrency = (amount) => {
    if (amount >= 0.01) {
      return `$${amount.toFixed(2)}`;
    } else if (amount >= 0.001) {
      return `$${amount.toFixed(3)}`;
    } else if (amount > 0) {
      return `$${amount.toFixed(6)}`;
    }
    return "$0.00";
  };

  const copyToClipboard = (text, type = "text") => {
    navigator.clipboard.writeText(text);
    const messages = {
      email: "Email copied to clipboard!",
      phone: "Phone number copied to clipboard!",
      location: "Location copied to clipboard!",
      linkedin: "LinkedIn URL copied to clipboard!",
      github: "GitHub URL copied to clipboard!",
      website: "Website URL copied to clipboard!",
      resumeId: "Resume ID copied to clipboard!",
      text: "Text copied to clipboard!",
    };

    // Determine message type based on content or provided type
    let messageType = type;
    if (text && text.includes("@")) messageType = "email";
    else if (text && text.includes("linkedin.com")) messageType = "linkedin";
    else if (text && text.includes("github.com")) messageType = "github";
    else if (text && (text.includes("+") || /^\d{3}-?\d{3}-?\d{4}$/.test(text))) messageType = "phone";

    message.success(messages[messageType] || messages.text);
  };

  // Function to validate if a string is a valid LinkedIn URL
  const isValidLinkedInURL = (string) => {
    if (!string || typeof string !== "string") return false;

    const linkedinPatterns = [
      /^https?:\/\/(www\.)?linkedin\.com\//i, // https://linkedin.com/ or https://www.linkedin.com/
      /^www\.linkedin\.com\//i, // www.linkedin.com/
      /^linkedin\.com\//i, // linkedin.com/
    ];

    return linkedinPatterns.some((pattern) => pattern.test(string.trim()));
  };

  // Function to validate if a string is a valid GitHub URL
  const isValidGitHubURL = (string) => {
    if (!string || typeof string !== "string") return false;

    const githubPatterns = [
      /^https?:\/\/(www\.)?github\.com\//i, // https://github.com/ or https://www.github.com/
      /^www\.github\.com\//i, // www.github.com/
      /^github\.com\//i, // github.com/
    ];

    return githubPatterns.some((pattern) => pattern.test(string.trim()));
  };

  // Function to validate if a string is a valid website URL
  const isValidWebsiteURL = (string) => {
    if (!string || typeof string !== "string") return false;

    const websitePatterns = [
      /^https?:\/\//i, // starts with http:// or https://
      /^www\./i, // starts with www.
      /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/i, // domain pattern (something.com or something.com/path)
    ];

    // Check if it matches any website pattern
    const matchesPattern = websitePatterns.some((pattern) => pattern.test(string.trim()));

    // Additional check: if it contains spaces or common non-URL phrases, it's likely not a URL
    const hasSpaces = /\s/.test(string.trim());
    const isInstruction = /add|create|insert|enter|input|provide|update/i.test(string);

    // If it has spaces and doesn't start with http/www, it's probably not a URL
    if (hasSpaces && !(/^https?:\/\//i.test(string) || /^www\./i.test(string))) {
      return false;
    }

    // If it contains instruction words, it's probably not a URL
    if (isInstruction) {
      return false;
    }

    return matchesPattern;
  };

  const handleAccessResume = async (record) => {
    try {
      // Get admin access token for this specific resume
      const response = await fetch.get(`admin/resume-access-token?resumeId=${record.resumeId}`);

      if (response.success && response.data?.token) {
        // Open resume with admin access token
        const baseUrl = window.location.hostname === "localhost" ? "https://localhost:4200" : window.location.origin;
        window.open(`${baseUrl}/app/resume/${record.resumeId}?adminToken=${response.data.token}`, "_blank");
      } else {
        message.error(response.message || "Failed to generate admin access token");
      }
    } catch (error) {
      console.error("Error accessing resume:", error);
      message.error("Failed to access resume. Please try again.");
    }
  };

  const handleViewFileURL = (record) => {
    if (record.fileUrl) {
      // Open file URL in new tab
      window.open(record.fileUrl, "_blank");
    } else {
      message.warning("File URL not available for this resume.");
    }
  };

  const handleTestAIPrompt = (record) => {
    setSelectedResumeForAI(record);
    setAiPromptModalVisible(true);
  };

  // Handle toggle public status of resume (Admin only)
  const handleTogglePublicStatus = async (resumeId, isPublic) => {
    try {
      const response = await fetch.put(`admin/resume/${resumeId}/toggle-public`, {
        isPublic: isPublic
      });

      if (response.success) {
        // Update local state
        setData(prevData =>
          prevData.map(item =>
            item.resumeId === resumeId
              ? {
                  ...item,
                  isPublic: isPublic,
                  publicSlug: isPublic ? (response.data?.publicSlug || item.publicSlug) : null,
                  madePublicAt: isPublic ? new Date() : null
                }
              : item
          )
        );
        // Update selected public resume state if modal is open
        if (selectedPublicResume && selectedPublicResume.resumeId === resumeId) {
          setSelectedPublicResume(prev => ({
            ...prev,
            isPublic: isPublic,
            publicSlug: isPublic ? (response.data?.publicSlug || prev.publicSlug) : null,
          }));
        }
        message.success(`Resume ${isPublic ? 'made public' : 'made private'} successfully!`);
        return true;
      } else {
        message.error(response.message || 'Failed to update public status');
        return false;
      }
    } catch (error) {
      console.error('Error toggling public status:', error);
      message.error('Failed to update public status');
      return false;
    }
  };

  // Load public resume settings when modal opens
  const loadPublicResumeSettings = async (resumeId) => {
    try {
      const response = await fetch.get(`admin/resume/${resumeId}/public-settings`);
      if (response.success && response.data) {
        setPublicResumeSettings({
          showEmail: response.data.publicSettings?.showEmail ?? true,
          showPhone: response.data.publicSettings?.showPhone ?? true,
          showAddress: response.data.publicSettings?.showAddress ?? true,
        });
      }
    } catch (error) {
      console.error("Error loading public settings:", error);
    }
  };

  // Save public resume settings
  const handleSavePublicResumeSettings = async () => {
    if (!selectedPublicResume) return;

    setPublicResumeLoading(true);
    try {
      const response = await fetch.put(`admin/resume/${selectedPublicResume.resumeId}/public-settings`, {
        publicSettings: publicResumeSettings,
      });

      if (response.success) {
        message.success("Privacy settings updated!");
      } else {
        message.error(response.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving public settings:", error);
      message.error("Failed to save settings. Please try again.");
    } finally {
      setPublicResumeLoading(false);
    }
  };

  // Handle opening public resume modal
  const handleOpenPublicResumeModal = (record) => {
    setSelectedPublicResume({
      resumeId: record.resumeId,
      isPublic: record.isPublic,
      publicSlug: record.publicSlug,
      userName: record.userName,
    });
    setPublicResumeModalVisible(true);
    loadPublicResumeSettings(record.resumeId);
  };

  // Handle training data export
  const handleExportTrainingData = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: "1",
        pageSize: "10000", // Export all filtered data
      });

      // Add current filter parameters
      if (searchTerm && searchTerm.trim()) {
        queryParams.append("search", searchTerm.trim());
      }

      if (filters.fileType) {
        queryParams.append("fileType", filters.fileType);
      }
      if (filters.userType) {
        queryParams.append("userType", filters.userType);
      }
      if (filters.createdDate) {
        queryParams.append("createdDate", filters.createdDate);
      }
      if (filters.sortOrder) {
        queryParams.append("sortOrder", filters.sortOrder);
      }
      if (filters.limitCount) {
        queryParams.append("limitCount", filters.limitCount);
      }
      if (filters.formUses) {
        queryParams.append("formUses", filters.formUses);
      }
      if (filters.hasPreview) {
        queryParams.append("hasPreview", filters.hasPreview);
      }
      if (filters.hasShare) {
        queryParams.append("hasShare", filters.hasShare);
      }
      if (filters.hasThankYou) {
        queryParams.append("hasThankYou", filters.hasThankYou);
      }
      if (filters.isPublic) {
        queryParams.append("isPublic", filters.isPublic);
      }
      if (filters.hasCoverLetter) {
        queryParams.append("hasCoverLetter", filters.hasCoverLetter);
      }
      // Call the export API
      const response = await fetch.get(`admin/export-training-data?${queryParams.toString()}`);

      if (response.success && response.data) {
        // Create and download JSONL file (required for OpenAI fine-tuning)
        const trainingData = response.data;

        // Convert to JSONL format (one JSON object per line)
        const jsonlString = trainingData.map((item) => JSON.stringify(item)).join("\n");
        const blob = new Blob([jsonlString], { type: "application/jsonl" });
        const url = window.URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;

        // Generate filename with current date and filter info
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0];
        const filterInfo = Object.values(filters).filter(Boolean).join("-") || "all";
        const filename = `resume-training-data-${dateStr}-${filterInfo}.jsonl`;

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success(`Successfully exported ${trainingData.length} training examples to ${filename}`);
      } else {
        throw new Error(response.message || "Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
        return "processing";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "No",
      dataIndex: "id",
      key: "no",
      width: 50,
      align: "center",
      render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "User Info",
      key: "userInfo",
      width: 250,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space size="middle">
            <Avatar size="small" icon={<UserOutlined />} />
            <Text strong>{record.userName || "null"}</Text>
          </Space>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Resume ID: </Text>
            <Text style={{ color: "#1890ff", fontSize: "11px", fontFamily: "monospace" }}>{record.resumeId}</Text>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(record.resumeId, "resumeId")}
              style={{ marginLeft: "4px", padding: "0 4px" }}
            />
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Email: </Text>
            <Text style={{ color: "#000" }}>{record.userEmail || "null"}</Text>
            {record.userEmail && (
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(record.userEmail)}
                style={{ marginLeft: "4px", padding: "0 4px" }}
              />
            )}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Phone: </Text>
            <Text style={{ color: "#000" }}>{record.userPhone || "null"}</Text>
            {record.userPhone && (
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(record.userPhone)}
                style={{ marginLeft: "4px", padding: "0 4px" }}
              />
            )}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Location: </Text>
            <Text style={{ color: "#000" }}>{record.location || "null"}</Text>
          </div>

          {/* <div>
            <Text style={{ color: '#000', fontWeight: '500' }}>LinkedIn: </Text>
            {record.linkedIn && isValidLinkedInURL(record.linkedIn) ? (
              <>
                <a 
                  href={record.linkedIn.startsWith('http') ? record.linkedIn : `https://${record.linkedIn}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                  {record.linkedIn}
                </a>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(record.linkedIn)}
                  style={{ marginLeft: '4px', padding: '0 4px' }}
                />
              </>
            ) : (
              <Text style={{ color: '#000' }}>null</Text>
            )}
          </div> */}

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>GitHub: </Text>
            {record.github && isValidGitHubURL(record.github) ? (
              <>
                <a
                  href={record.github.startsWith("http") ? record.github : `https://${record.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1890ff", textDecoration: "underline" }}
                >
                  {record.github}
                </a>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(record.github)}
                  style={{ marginLeft: "4px", padding: "0 4px" }}
                />
              </>
            ) : (
              <Text style={{ color: "#000" }}>null</Text>
            )}
          </div>

          {/* <div>
            <Text style={{ color: '#000', fontWeight: '500' }}>Website: </Text>
            {record.website && isValidWebsiteURL(record.website) ? (
              <>
                <a 
                  href={record.website.startsWith('http') ? record.website : `https://${record.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                  {record.website}
                </a>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(record.website)}
                  style={{ marginLeft: '4px', padding: '0 4px' }}
                />
              </>
            ) : (
              <Text style={{ color: '#000' }}>null</Text>
            )}
          </div> */}
        </Space>
      ),
    },
    {
      title: "Resume Details",
      key: "resumeDetails",
      width: 240,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>File Name: </Text>
            <Text style={{ color: "#000" }}>{record.fileName || "null"}</Text>
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Source: </Text>
            {(() => {
              // Determine source: use resumeSource field first, fallback to legacy detection
              const source = record.resumeSource
                || (record.creationMode === "blank" && record.initialContext?.source === "naukri-job-input" ? "naukri" : null)
                || (record.creationMode === "upload" ? "upload" : null)
                || (record.creationMode === "blank" ? "blank" : "upload");

              const tagProps = {
                naukri: { color: "cyan", label: "Naukri" },
                "five-sec-score": { color: "purple", label: "5 Sec Resume" },
                upload: { color: "blue", label: "Upload File" },
                blank: { color: "green", label: "Generate Resume" },
              }[source] || { color: "default", label: "Unknown" };

              const hasFileDetails = source === "upload" || source === "five-sec-score";

              return (
                <Tag
                  color={tagProps.color}
                  style={hasFileDetails ? { cursor: "pointer" } : {}}
                  onClick={hasFileDetails ? () => setSourceDetailsModal({ visible: true, record }) : undefined}
                >
                  {tagProps.label}
                </Tag>
              );
            })()}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>PreviewOpen: </Text>
            <Text style={{ color: record.hasPreview ? "#52c41a" : "#ff4d4f" }}>
              {record.hasPreview ? "Yes" : "No"}
            </Text>
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>Shared: </Text>
            <Text style={{ color: record.hasShare ? "#52c41a" : "#ff4d4f" }}>
              {record.hasShare ? "Yes" : "No"}
            </Text>
            {record.hasShare && record.sharedUrl && record.platformCode && (
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => window.open(record.sharedUrl, "_blank")}
                title="Open shared URL"
                style={{
                  marginLeft: "4px",
                  padding: "0 4px",
                  color: "#1890ff",
                  fontSize: "12px",
                }}
              >
                {record.platformCode.toLowerCase()}
              </Button>
            )}
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>ThankYou: </Text>
            <Text style={{ color: record.hasThankYou ? "#52c41a" : "#ff4d4f" }}>
              {record.hasThankYou ? "Yes" : "No"}
            </Text>
          </div>

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>PublicResume: </Text>
            <Text style={{ color: record.isPublic ? "#52c41a" : "#ff4d4f" }}>
              {record.isPublic ? "Yes" : "No"}
            </Text>
            {record.isPublic && record.publicSlug && (
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => {
                  const baseUrl =
                    window.location.hostname === "localhost" ? "https://localhost:4200" : window.location.origin;
                  window.open(`${baseUrl}/r/${record.publicSlug}`, "_blank");
                }}
                title="Open public resume"
                style={{
                  marginLeft: "4px",
                  padding: "0 4px",
                  color: "#1890ff",
                  fontSize: "12px",
                }}
              >
                view
              </Button>
            )}
            <Button
              type="link"
              size="small"
              onClick={() => handleOpenPublicResumeModal(record)}
              style={{ padding: 0, marginLeft: 8 }}
            >
              settings
            </Button>
          </div>
        </Space>
      ),
    },
    {
      title: "Tokens Usage & Pricing",
      key: "tokenUsage",
      width: 320,
      render: (_, record) => {
        const parsingCost = calculateParsingCost(record.parsingInputTokens, record.parsingOutputTokens);
        const chatCost = calculateChatCost(record.chatInputTokens, record.chatOutputTokens);
        const enhancementCost = calculateEnhancementCost(
          record.enhancementInputTokens,
          record.enhancementOutputTokens
        );
        const coverLetterCost = calculateTokenCost(record.coverLetterInputTokens, record.coverLetterOutputTokens);
        const naukriCost = calculateTokenCost(record.naukriInputTokens, record.naukriOutputTokens);
        const totalCost = parsingCost + chatCost + enhancementCost + coverLetterCost + naukriCost;

        const tokensData = [
          {
            key: "parsing",
            type: "Parsing",
            input: record.parsingInputTokens,
            output: record.parsingOutputTokens,
            total: record.parsingTotalTokens,
            cost: parsingCost,
          },
          {
            key: "chat",
            type: "Chat",
            input: record.chatInputTokens,
            output: record.chatOutputTokens,
            total: record.chatTotalTokens,
            cost: chatCost,
          },
          {
            key: "enhancement",
            type: "Form Edit",
            input: record.enhancementInputTokens,
            output: record.enhancementOutputTokens,
            total: record.enhancementTotalTokens,
            cost: enhancementCost,
          },
          {
            key: "coverLetter",
            type: "Cover Letter",
            input: record.coverLetterInputTokens,
            output: record.coverLetterOutputTokens,
            total: record.coverLetterTotalTokens,
            cost: coverLetterCost,
          },
          {
            key: "naukri",
            type: "Naukri Job",
            input: record.naukriInputTokens,
            output: record.naukriOutputTokens,
            total: record.naukriTotalTokens,
            cost: naukriCost,
          },
          {
            key: "total",
            type: "Total",
            input: record.totalInputTokens,
            output: record.totalOutputTokens,
            total: record.grandTotalTokens,
            cost: record.totalCost || totalCost,
          },
        ];

        const columns = [
          {
            title: "Type",
            dataIndex: "type",
            width: "20%",
            align: "center",
            render: (text, rec) => (
              <Text
                style={{
                  fontSize: "11px",
                  fontWeight: rec.key === "total" ? "600" : "500",
                  color: rec.key === "total" ? "#1890ff" : "#000",
                }}
              >
                {text}
              </Text>
            ),
          },
          {
            title: "In",
            dataIndex: "input",
            width: "20%",
            align: "center",
            render: (value, rec) => (
              <Text
                style={{
                  fontSize: "10px",
                  color: rec.key === "total" ? "#1890ff" : "#666",
                }}
              >
                {value.toLocaleString()}
              </Text>
            ),
          },
          {
            title: "Out",
            dataIndex: "output",
            width: "20%",
            align: "center",
            render: (value, rec) => (
              <Text
                style={{
                  fontSize: "10px",
                  color: rec.key === "total" ? "#1890ff" : "#666",
                }}
              >
                {value.toLocaleString()}
              </Text>
            ),
          },
          {
            title: "Total",
            dataIndex: "total",
            width: "20%",
            align: "center",
            render: (value, rec) => (
              <Text
                style={{
                  fontSize: "10px",
                  fontWeight: rec.key === "total" ? "600" : "500",
                  color: rec.key === "total" ? "#1890ff" : "#000",
                }}
              >
                {value.toLocaleString()}
              </Text>
            ),
          },
          {
            title: "Cost",
            dataIndex: "cost",
            width: "20%",
            align: "center",
            render: (value, rec) => (
              <Text
                style={{
                  fontSize: "10px",
                  fontWeight: rec.key === "total" ? "600" : "500",
                  color: rec.key === "total" ? "#52c41a" : "#999",
                }}
              >
                {formatCurrency(value)}
              </Text>
            ),
          },
        ];

        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
            }}
          >
            <style>
              {`
                .tokens-usage-table .ant-table {
                  width: 100% !important;
                }
                .tokens-usage-table .ant-table-thead > tr > th {
                  padding: 4px 6px !important;
                  font-size: 10px !important;
                  font-weight: 600 !important;
                  text-align: center !important;
                  border-bottom: 1px solid #d9d9d9 !important;
                }
                .tokens-usage-table .ant-table-tbody > tr > td {
                  padding: 2px 6px !important;
                  font-size: 10px !important;
                  text-align: center !important;
                }
                .tokens-usage-table .ant-table-container {
                  width: 100% !important;
                }
                .tokens-usage-table .ant-table-content {
                  width: 100% !important;
                }
              `}
            </style>
            <Table
              dataSource={tokensData}
              columns={columns}
              pagination={false}
              size="small"
              showHeader={true}
              style={{
                width: "100%",
              }}
              tableLayout="fixed"
              className="tokens-usage-table"
            />
            {/* AI Usage Summary */}
            <div
              style={{
                marginTop: "8px",
                textAlign: "center",
                width: "100%",
                borderTop: "1px solid #f0f0f0",
                paddingTop: "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <Text style={{ fontSize: "9px", color: "#666", display: "block" }}>Chat Uses</Text>
                  <Text style={{ fontSize: "12px", fontWeight: "600", color: "#1890ff" }}>
                    {record.chatInputCount || 0}
                  </Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <Text style={{ fontSize: "9px", color: "#666", display: "block" }}>Form Uses</Text>
                  <Text style={{ fontSize: "12px", fontWeight: "600", color: "#52c41a" }}>
                    {record.formEditInputCount || 0}
                  </Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <Text style={{ fontSize: "9px", color: "#666", display: "block" }}>Total Uses</Text>
                  <Text style={{ fontSize: "12px", fontWeight: "600", color: "#722ed1" }}>
                    {(record.chatInputCount || 0) + (record.formEditInputCount || 0)}
                  </Text>
                </div>
              </div>
            </div>

            {(record.lastChatUpdate || record.lastEnhancementUpdate) && (
              <div style={{ marginTop: "4px", textAlign: "center", width: "100%" }}>
                {record.lastChatUpdate && (
                  <Text style={{ color: "#999", fontSize: "9px", display: "block" }}>
                    Last chat: {formatTimeAgo(record.lastChatUpdate)}
                  </Text>
                )}
                {record.lastEnhancementUpdate && (
                  <Text style={{ color: "#999", fontSize: "9px", display: "block" }}>
                    Last edit: {formatTimeAgo(record.lastEnhancementUpdate)}
                  </Text>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {/* Action Buttons */}
          <Tooltip title="Access Resume Chat">
            <Button
              size="small"
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleAccessResume(record)}
              style={{ width: "100%" }}
            >
              Access
            </Button>
          </Tooltip>
          <Tooltip title="View Uploaded File URL">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => handleViewFileURL(record)}
              disabled={!record.fileUrl}
              style={{ width: "100%" }}
            >
              File URL
            </Button>
          </Tooltip>
          <Tooltip title="Test AI Prompts">
            <Button
              size="small"
              icon={<ExperimentOutlined />}
              onClick={() => handleTestAIPrompt(record)}
              style={{
                borderColor: "#52c41a",
                color: "#52c41a",
                width: "100%",
              }}
            >
              AI Test
            </Button>
          </Tooltip>

          {/* Status Information */}
          <div
            style={{
              marginTop: "8px",
              padding: "8px",
              backgroundColor: "#fafafa",
              borderRadius: "4px",
              borderTop: "2px solid #f0f0f0",
            }}
          >
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text style={{ fontSize: "10px", color: "#666", fontWeight: "500" }}>Created:</Text>
                <Text style={{ fontSize: "10px", color: "#000" }}>{formatTimeAgo(record.createdAt)}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text style={{ fontSize: "10px", color: "#666", fontWeight: "500" }}>Updated:</Text>
                <Text style={{ fontSize: "10px", color: "#000" }}>{formatTimeAgo(record.updatedAt)}</Text>
              </div>

              {record.parseDate && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text style={{ fontSize: "10px", color: "#666", fontWeight: "500" }}>Parsed:</Text>
                  <Text style={{ fontSize: "10px", color: "#000" }}>{formatTimeAgo(record.parseDate)}</Text>
                </div>
              )}

              {record.processingTime && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text style={{ fontSize: "10px", color: "#666", fontWeight: "500" }}>Processing:</Text>
                  <Text style={{ fontSize: "10px", color: "#000" }}>
                    {(record.processingTime / 1000).toFixed(1)}s
                  </Text>
                </div>
              )}
            </Space>
          </div>
        </Space>
      ),
    },
  ];

  // Debounced search function
  const debouncedSearch = useCallback(
    (value) => {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for search
      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page when searching
        // Fetch data with new search term
        fetchResumeData(1, pageSize, value, filters);
      }, 500); // 500ms debounce delay
    },
    [pageSize, filters]
  );

  const handleSearch = (value) => {
    debouncedSearch(value);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value); // Update UI immediately
    debouncedSearch(value); // Debounce API call
  };

  // Handle search clear
  const handleSearchClear = () => {
    // Clear timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchResumeData(1, pageSize, "", filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
    // Fetch data with new filters
    fetchResumeData(1, pageSize, searchTerm, newFilters);
  };

  // Pagination calculations - now server-side
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const currentPageData = data; // Data is already paginated from server

  // Pagination handlers - now trigger server requests
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchResumeData(page, pageSize, searchTerm, filters);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchResumeData(newPage, pageSize, searchTerm, filters);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchResumeData(newPage, pageSize, searchTerm, filters);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 6) {
        for (let i = 1; i <= maxVisiblePages; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 5) {
        for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 5; i <= currentPage + 4; i++) {
          pages.push(i);
        }
      }
    }

    return pages;
  };

  return (
    <Layout.Content style={{ padding: "24px 24px 24px 24px", width: "100%", overflow: "hidden" }}>
      <style>{`
        .resume-data-search .ant-input::placeholder,
        .resume-data-search .ant-input-affix-wrapper input::placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
        
        .resume-data-filter .ant-select-selector .ant-select-selection-placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden", marginTop: "12px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header with Search Bar and Refresh Button */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            {/* Search Bar */}
            <div style={{ flex: 1, maxWidth: "800px" }}>
              <Search
                className="resume-data-search"
                placeholder="Search by name, email, phone, location, file name, resume ID, public URL (use filters for cover letter)"
                allowClear
                enterButton="Search"
                size="large"
                value={searchInput}
                onSearch={handleSearch}
                onChange={handleSearchInputChange}
                onClear={handleSearchClear}
                style={{
                  width: "100%",
                }}
              />
            </div>

            {/* Export Button, Refresh Button and Total Count */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Tooltip title="Export Training Data (JSON format for AI model training)">
                <Button
                  icon={<ExportOutlined />}
                  size="large"
                  loading={exportLoading}
                  onClick={handleExportTrainingData}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderColor: "#52c41a",
                    color: "#52c41a",
                  }}
                >
                  Export Training Data
                </Button>
              </Tooltip>

              <Tooltip title="Refresh Data">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  size="large"
                  loading={loading}
                  onClick={() => fetchResumeData(currentPage, pageSize, searchTerm, filters)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>

              {/* Total Count Display */}
              <div
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                  minWidth: "200px",
                  textAlign: "center",
                }}
              >
                <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                  {filters.fileType ||
                  filters.userType ||
                  filters.createdDate ||
                  filters.limitCount ||
                  filters.formUses ||
                  filters.hasPreview ||
                  filters.hasShare ||
                  filters.hasThankYou ||
                  filters.isPublic ||
                  filters.hasCoverLetter ||
                  filters.resumeSource
                    ? "Filtered: "
                    : "Total: "}
                  {totalItems.toLocaleString()}
                </Text>
                <Text style={{ fontSize: "12px", color: "#666", marginLeft: "4px" }}>
                  resume{totalItems === 1 ? "" : "s"}
                </Text>
                {filters.limitCount && (
                  <Text style={{ fontSize: "10px", color: "#999", display: "block", marginTop: "2px" }}>
                    Limited to top {filters.limitCount}
                  </Text>
                )}
                {filters.sortOrder && filters.sortOrder !== "latest" && (
                  <Text style={{ fontSize: "10px", color: "#999", display: "block" }}>
                    {filters.sortOrder === "oldest" ? "Oldest first" : "Latest first"}
                  </Text>
                )}
              </div>
            </div>
          </div>

          {/* Filter Row */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="File Type"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("fileType", value)}
              >
                <Option value="pdf">PDF</Option>
                <Option value="docx">DOCX</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="Source"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("resumeSource", value)}
              >
                <Option value="upload">Upload File</Option>
                <Option value="blank">Generate Resume</Option>
                <Option value="naukri">Naukri</Option>
                <Option value="five-sec-score">5 Sec Resume</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="User Type"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("userType", value)}
              >
                <Option value="registered">Registered</Option>
                <Option value="anonymous">Anonymous</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="Created Date"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("createdDate", value)}
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="last_3_days">Last 3 Days</Option>
                <Option value="last_7_days">Last 7 Days</Option>
                <Option value="last_15_days">Last 15 Days</Option>
                <Option value="last_30_days">Last 30 Days</Option>
                <Option value="last_60_days">Last 60 Days</Option>
                <Option value="last_90_days">Last 90 Days</Option>
                <Option value="last_6_months">Last 6 Months</Option>
                <Option value="last_year">Last Year</Option>
                <Option value="week">This Week</Option>
                <Option value="month">This Month</Option>
                <Option value="year">This Year</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="Form Uses"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("formUses", value)}
              >
                <Option value="0">0 Form Uses</Option>
                <Option value="1">1+ Form Uses</Option>
                <Option value="5">5+ Form Uses</Option>
                <Option value="10">10+ Form Uses</Option>
                <Option value="20">20+ Form Uses</Option>
                <Option value="50">50+ Form Uses</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="Sort Order"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.sortOrder}
                onChange={(value) => handleFilterChange("sortOrder", value)}
              >
                <Option value="latest">Latest First</Option>
                <Option value="oldest">Oldest First</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Select
                className="resume-data-filter"
                placeholder="Limit"
                allowClear
                size="small"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("limitCount", value)}
              >
                <Option value="10">Top 10</Option>
                <Option value="25">Top 25</Option>
                <Option value="50">Top 50</Option>
                <Option value="100">Top 100</Option>
                <Option value="250">Top 250</Option>
                <Option value="500">Top 500</Option>
                <Option value="1000">Top 1000</Option>
              </Select>
            </div>
          </div>
          <Row gutter={12} style={{ marginBottom: "16px" }}>
            <Col span={4}>
              <Select
                className="resume-data-filter"
                placeholder="Preview"
                allowClear
                size="middle"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("hasPreview", value)}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                className="resume-data-filter"
                placeholder="Shared"
                allowClear
                size="middle"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("hasShare", value)}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                className="resume-data-filter"
                placeholder="ThankYou"
                allowClear
                size="middle"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("hasThankYou", value)}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                className="resume-data-filter"
                placeholder="Public Resume"
                allowClear
                size="middle"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("isPublic", value)}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                className="resume-data-filter"
                placeholder="Cover Letter"
                allowClear
                size="middle"
                style={{ width: "100%" }}
                onChange={(value) => handleFilterChange("hasCoverLetter", value)}
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Col>
          </Row>

          {/* Active Filters Display */}
          {(filters.fileType ||
            filters.resumeSource ||
            filters.userType ||
            filters.createdDate ||
            filters.limitCount ||
            filters.formUses ||
            filters.hasPreview ||
            filters.hasShare ||
            filters.hasThankYou ||
            filters.isPublic ||
            filters.hasCoverLetter) && (
            <Row style={{ marginBottom: "16px" }}>
              <Col span={24}>
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  <Text strong style={{ marginRight: "8px", color: "#666" }}>
                    Active Filters:
                  </Text>
                  {filters.fileType && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("fileType", "")}
                      color="green"
                      style={{ marginRight: "4px" }}
                    >
                      Type: {filters.fileType.toUpperCase()}
                    </Tag>
                  )}
                  {filters.resumeSource && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("resumeSource", "")}
                      color="cyan"
                      style={{ marginRight: "4px" }}
                    >
                      Source:{" "}
                      {filters.resumeSource === "upload"
                        ? "Upload File"
                        : filters.resumeSource === "blank"
                        ? "Generate Resume"
                        : filters.resumeSource === "naukri"
                        ? "Naukri"
                        : "5 Sec Resume"}
                    </Tag>
                  )}
                  {filters.userType && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("userType", "")}
                      color="orange"
                      style={{ marginRight: "4px" }}
                    >
                      Users: {filters.userType === "registered" ? "Registered" : "Anonymous"}
                    </Tag>
                  )}
                  {filters.createdDate && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("createdDate", "")}
                      color="purple"
                      style={{ marginRight: "4px" }}
                    >
                      Date: {filters.createdDate.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Tag>
                  )}
                  {filters.sortOrder && filters.sortOrder !== "latest" && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("sortOrder", "latest")}
                      color="cyan"
                      style={{ marginRight: "4px" }}
                    >
                      Sort: {filters.sortOrder === "latest" ? "Latest First" : "Oldest First"}
                    </Tag>
                  )}
                  {filters.limitCount && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("limitCount", "")}
                      color="red"
                      style={{ marginRight: "4px" }}
                    >
                      Limit: Top {filters.limitCount}
                    </Tag>
                  )}
                  {filters.formUses && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("formUses", "")}
                      color="magenta"
                      style={{ marginRight: "4px" }}
                    >
                      Form Uses: {filters.formUses === "0" ? "0" : `${filters.formUses}+`}
                    </Tag>
                  )}
                  {filters.hasPreview && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("hasPreview", "")}
                      color="volcano"
                      style={{ marginRight: "4px" }}
                    >
                      Preview: {filters.hasPreview === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.hasShare && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("hasShare", "")}
                      color="geekblue"
                      style={{ marginRight: "4px" }}
                    >
                      Shared: {filters.hasShare === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.hasThankYou && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("hasThankYou", "")}
                      color="lime"
                      style={{ marginRight: "4px" }}
                    >
                      ThankYou: {filters.hasThankYou === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.isPublic && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("isPublic", "")}
                      color="purple"
                      style={{ marginRight: "4px" }}
                    >
                      Public: {filters.isPublic === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  {filters.hasCoverLetter && (
                    <Tag
                      closable
                      onClose={() => handleFilterChange("hasCoverLetter", "")}
                      color="cyan"
                      style={{ marginRight: "4px" }}
                    >
                      Cover Letter: {filters.hasCoverLetter === "true" ? "Yes" : "No"}
                    </Tag>
                  )}
                  <Button
                    type="text"
                    size="small"
                    style={{ marginLeft: "8px", color: "#666" }}
                    onClick={() => {
                      setFilters({
                        fileType: "",
                        userType: "",
                        createdDate: "",
                        sortOrder: "latest",
                        limitCount: "",
                        formUses: "",
                        hasPreview: "",
                        hasShare: "",
                        hasThankYou: "",
                        isPublic: "",
                        hasCoverLetter: "",
                        resumeSource: "",
                      });
                      setCurrentPage(1);
                      fetchResumeData(1, pageSize, searchTerm, {
                        fileType: "",
                        userType: "",
                        createdDate: "",
                        sortOrder: "latest",
                        limitCount: "",
                        formUses: "",
                        hasPreview: "",
                        hasShare: "",
                        hasThankYou: "",
                        isPublic: "",
                        hasCoverLetter: "",
                        resumeSource: "",
                      });
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          {/* Data Table */}
          <Table
            columns={columns}
            dataSource={currentPageData}
            loading={loading}
            rowKey="id"
            pagination={false}
            size="middle"
          />

          {/* Page Size Selector */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "16px",
              gap: "8px",
            }}
          >
            <Text style={{ fontSize: "14px", color: "#666" }}>Items per page:</Text>
            <Select
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1); // Reset to first page when page size changes
                fetchResumeData(1, value, searchTerm, filters);
              }}
              style={{ width: "80px" }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </div>

          {/* Custom Pagination */}
          {totalItems > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              {/* Previous Button */}
              <Button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                style={{
                  minWidth: "80px",
                  height: "32px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "#fff",
                  color: currentPage === 1 ? "#d9d9d9" : "#1890ff",
                  marginRight: "8px",
                }}
              >
                Previous
              </Button>

              {/* Page Numbers */}
              {totalPages > 1 &&
                getPageNumbers().map((pageNum) => (
                  <Button
                    key={pageNum}
                    type={currentPage === pageNum ? "primary" : "default"}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      minWidth: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: currentPage === pageNum ? "none" : "1px solid #d9d9d9",
                      backgroundColor: currentPage === pageNum ? "#1890ff" : "transparent",
                      color: currentPage === pageNum ? "#fff" : "#262626",
                    }}
                  >
                    {pageNum}
                  </Button>
                ))}

              {/* Show page info when only one page */}
              {totalPages === 1 && (
                <span
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    fontWeight: "500",
                    margin: "0 16px",
                  }}
                >
                  Page 1 of 1
                </span>
              )}

              {/* Next Button */}
              <Button
                disabled={currentPage === totalPages}
                onClick={handleNext}
                style={{
                  minWidth: "80px",
                  height: "32px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === totalPages ? "#f5f5f5" : "#fff",
                  color: currentPage === totalPages ? "#d9d9d9" : "#1890ff",
                  marginLeft: "8px",
                }}
              >
                Next
              </Button>
            </div>
          )}

          {/* Show count info */}
          <div
            style={{
              textAlign: "center",
              marginTop: "16px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Showing {Math.min(startIndex + 1, totalItems)}-{endIndex} of {totalItems} resume
            {totalItems === 1 ? "" : "s"}
            {totalItems > 0 && (
              <span style={{ marginLeft: "16px", color: "#999" }}>
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </Space>
      </div>

      {/* AI Prompt Testing Modal */}
      <AIPromptTestingModal
        visible={aiPromptModalVisible}
        onCancel={() => {
          setAiPromptModalVisible(false);
          setSelectedResumeForAI(null);
        }}
        resumeData={selectedResumeForAI ? [selectedResumeForAI] : []}
      />

      {/* Review Modal */}
      {/* File Details Modal (for Upload File / 5 Sec Resume sources) */}
      <Modal
        open={sourceDetailsModal.visible}
        title="File Details"
        onCancel={() => setSourceDetailsModal({ visible: false, record: null })}
        footer={null}
        width={400}
        centered
      >
        {sourceDetailsModal.record && (
          <div style={{ padding: "10px 0" }}>
            <div style={{ marginBottom: 12 }}>
              <Text style={{ fontWeight: "500" }}>File Name: </Text>
              <Text>{sourceDetailsModal.record.fileName || "null"}</Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text style={{ fontWeight: "500" }}>File Size: </Text>
              <Text>
                {sourceDetailsModal.record.fileSize && sourceDetailsModal.record.fileSize > 0
                  ? (sourceDetailsModal.record.fileSize / 1024).toFixed(1) + " KB"
                  : "null"}
              </Text>
            </div>
            <div>
              <Text style={{ fontWeight: "500" }}>File Type: </Text>
              <Text>{sourceDetailsModal.record.fileType || "unknown"}</Text>
            </div>
          </div>
        )}
      </Modal>

      {/* Public Resume Settings Modal */}
      <Modal
        open={publicResumeModalVisible}
        title={
          <Space>
            <SettingOutlined style={{ color: "#1890ff" }} />
            <span>Public Resume Settings</span>
          </Space>
        }
        onCancel={() => {
          setPublicResumeModalVisible(false);
          setSelectedPublicResume(null);
        }}
        footer={null}
        width={500}
        centered
      >
        {selectedPublicResume && (
          <div style={{ padding: "10px 0" }}>
            {/* User Info */}
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Resume: </Text>
              <Text strong>{selectedPublicResume.userName || "Unknown User"}</Text>
            </div>

            {/* Public/Private Toggle */}
            <Card
              size="small"
              style={{
                marginBottom: 24,
                border: selectedPublicResume.isPublic ? "2px solid #52c41a" : "2px solid #d9d9d9",
                backgroundColor: selectedPublicResume.isPublic ? "#f6ffed" : "#fafafa",
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Space direction="vertical" size={0}>
                    <Space>
                      {selectedPublicResume.isPublic ? (
                        <GlobalOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                      ) : (
                        <LockOutlined style={{ color: "#8c8c8c", fontSize: 18 }} />
                      )}
                      <Text strong style={{ fontSize: 16 }}>
                        {selectedPublicResume.isPublic ? "Public Resume" : "Private Resume"}
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 26 }}>
                      {selectedPublicResume.isPublic
                        ? "Anyone with the link can view this resume"
                        : "Only authorized users can view this resume"}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Switch
                    checked={selectedPublicResume.isPublic}
                    onChange={(checked) => handleTogglePublicStatus(selectedPublicResume.resumeId, checked)}
                    loading={publicResumeLoading}
                    checkedChildren={<GlobalOutlined />}
                    unCheckedChildren={<LockOutlined />}
                    style={{
                      backgroundColor: selectedPublicResume.isPublic ? "#52c41a" : undefined,
                    }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Public Link Section - Only show if public */}
            {selectedPublicResume.isPublic && selectedPublicResume.publicSlug && (
              <Card style={{ marginBottom: 24, backgroundColor: "#f5f5f5" }}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Text type="secondary" strong>
                    Public Link
                  </Text>
                  <Input
                    value={`${window.location.hostname === "localhost" ? "https://localhost:4200" : window.location.origin}/r/${selectedPublicResume.publicSlug}`}
                    readOnly
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                    addonAfter={
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          const url = `${window.location.hostname === "localhost" ? "https://localhost:4200" : window.location.origin}/r/${selectedPublicResume.publicSlug}`;
                          navigator.clipboard.writeText(url);
                          message.success("Link copied to clipboard!");
                        }}
                      >
                        Copy
                      </Button>
                    }
                  />
                  <Button
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => {
                      const baseUrl =
                        window.location.hostname === "localhost" ? "https://localhost:4200" : window.location.origin;
                      window.open(`${baseUrl}/r/${selectedPublicResume.publicSlug}`, "_blank");
                    }}
                    style={{ marginTop: 8 }}
                  >
                    View Public Page
                  </Button>
                </Space>
              </Card>
            )}

            {/* Contact Visibility Settings - Only show if public */}
            {selectedPublicResume.isPublic && (
              <>
                <Divider orientation="left" style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: 14 }}>Contact Visibility Settings</Text>
                </Divider>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text>Show Email Address</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Display email on public resume
                        </Text>
                      </Col>
                      <Col>
                        <Switch
                          checked={publicResumeSettings.showEmail}
                          onChange={(checked) =>
                            setPublicResumeSettings((prev) => ({ ...prev, showEmail: checked }))
                          }
                        />
                      </Col>
                    </Row>

                    <Divider style={{ margin: "8px 0" }} />

                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text>Show Phone Number</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Display phone on public resume
                        </Text>
                      </Col>
                      <Col>
                        <Switch
                          checked={publicResumeSettings.showPhone}
                          onChange={(checked) =>
                            setPublicResumeSettings((prev) => ({ ...prev, showPhone: checked }))
                          }
                        />
                      </Col>
                    </Row>

                    <Divider style={{ margin: "8px 0" }} />

                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text>Show Address/Location</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Display address on public resume
                        </Text>
                      </Col>
                      <Col>
                        <Switch
                          checked={publicResumeSettings.showAddress}
                          onChange={(checked) =>
                            setPublicResumeSettings((prev) => ({ ...prev, showAddress: checked }))
                          }
                        />
                      </Col>
                    </Row>
                  </Space>
                </Card>

                <Button
                  type="primary"
                  onClick={handleSavePublicResumeSettings}
                  loading={publicResumeLoading}
                  style={{ width: "100%" }}
                >
                  Save Contact Settings
                </Button>
              </>
            )}

            {/* Close Button */}
            <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}>
              <Button
                onClick={() => {
                  setPublicResumeModalVisible(false);
                  setSelectedPublicResume(null);
                }}
              >
                Close
              </Button>
            </Space>
          </div>
        )}
      </Modal>
    </Layout.Content>
  );
}

export default ResumeData;
